/**
 * Incidents Service
 * 
 * Handles all incident-related API operations.
 * Connects to func-axn-analyst-api via SWA linked backend.
 * 
 * API endpoints:
 * - GET  /api/incidents
 * - GET  /api/incidents/:tenantId/:incidentId
 * - GET  /api/incidents/:tenantId/:incidentId/entities
 * - GET  /api/incidents/:tenantId/:incidentId/history
 * - PATCH /api/incidents/:tenantId/:incidentId/status
 * - PATCH /api/incidents/:tenantId/:incidentId/assign
 */

import type {
  EnrichedIncident,
  PlatformStatus,
  IncidentFilters,
  IncidentStatusChange,
  Entity,
  TimelineEvent,
  MitreMapping,
  AnalystNote,
  DefenderClassification,
  DefenderDetermination,
} from '../data/types';
import { apiFetch, apiPatch, apiPost, buildQueryString, type ApiResponse } from './api';

// ============================================================================
// API Response Types
// ============================================================================

interface PaginatedIncidentsResponse {
  incidents: ApiIncident[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

interface ApiIncident {
  tenantId: string;
  incidentId: string;
  defenderIncidentId: string | null;
  defenderWebUrl: string | null;
  title: string;
  description: string | null;
  severity: string;
  defenderStatus: string | null;
  classification: string | null;
  determination: string | null;
  platformStatus: string;
  assignedToUserId: string | null;
  assignedToName: string | null;
  tenantDisplayName: string;
  createdTime: string;
  lastUpdated: string;
  summary: string | null;
  riskScore: number | null;
  reasoning: string | null;
  rawBlobPath: string | null;
  aiBlobPath: string | null;
  // New AI analysis fields
  aiClassification: string | null;
  aiConfidence: number | null;
  aiEscalate: boolean | null;
  aiImmediateAction: string | null;
  aiAffectedAsset: string | null;
  // Playbook routing fields
  playbookId: string | null;
  playbookName: string | null;
  incidentFamily: string | null;
  routingConfidence: number | null;
  enrichmentCount: number | null;
  enrichmentLatencyMs: number | null;
  enrichmentFailures: number | null;
  newAlertCount: number;
  newAlertsDetectedAt: string | null;
  reportCandidate?: boolean;
  reportCandidateBy?: string | null;
  reportCandidateAt?: string | null;
}

interface IncidentDetailResponse {
  incident: ApiIncident;
  timeline: Array<{
    eventId: string;
    title: string;
    description: string | null;
    timestamp: string;
    confidence: string | null;
    source: string;
  }>;
  mitre: Array<{
    tactic: string;
    technique: string;
    techniqueName: string | null;
    source: string;
  }>;
  tags: string[];
  entities: Array<{
    entityId: string;
    entityType: string;
    entityValue: string;
    displayName: string | null;
    additionalData: Record<string, unknown> | null;
    riskLevel: string | null;
  }>;
  notes: Array<{
    noteId: string;
    authorUserId: string;
    authorName: string;
    noteType: string;
    content: string;
    isInternal: boolean;
    isPinned: boolean;
    createdAt: string;
    updatedAt: string;
  }>;
}

interface EntitiesResponse {
  entities: Array<{
    entityId: string;
    entityType: string;
    entityValue: string;
    displayName: string | null;
    additionalData: Record<string, unknown> | null;
    riskLevel: string | null;
  }>;
}

interface StatusHistoryResponse {
  history: Array<{
    historyId: string;
    previousStatus: string | null;
    newStatus: string;
    changedByUserId: string | null;
    changedByName: string | null;
    reason: string | null;
    changedAt: string;
    actor?: 'user' | 'autopilot' | 'platform';
  }>;
}

interface UpdateStatusResponse {
  incident: ApiIncident;
  defenderSync?: { attempted: boolean; succeeded: boolean };
}

export type UpdateIncidentStatusResult = ApiResponse<EnrichedIncident> & {
  defenderSync?: { attempted: boolean; succeeded: boolean };
};

// ============================================================================
// TRANSFORM HELPERS
// ============================================================================

function transformApiIncident(api: ApiIncident): EnrichedIncident {
  return {
    schemaVersion: '0.1.0',
    status: api.platformStatus === 'closed' ? 'resolved' : 'active',
    tenantId: api.tenantId,
    incidentId: api.incidentId,
    severity: api.severity as EnrichedIncident['severity'],
    title: api.title,
    createdTime: api.createdTime,
    lastUpdated: api.lastUpdated,
    blobs: {
      raw: { path: api.rawBlobPath || '', url: '' },
      ai: { path: api.aiBlobPath || '', url: '' },
    },
    summary: api.summary,
    tenantDisplayName: api.tenantDisplayName,
    platformStatus: api.platformStatus as PlatformStatus,
    assignedToUserId: api.assignedToUserId,
    assignedToName: api.assignedToName,
    defenderStatus: api.defenderStatus || undefined,
    defenderWebUrl: api.defenderWebUrl,
    classification: api.classification || undefined,
    determination: api.determination || undefined,
    // AI analysis fields (direct on incident for easy access)
    riskScore: api.riskScore,
    reasoning: api.reasoning,
    aiClassification: api.aiClassification,
    aiConfidence: api.aiConfidence,
    aiEscalate: api.aiEscalate,
    aiImmediateAction: api.aiImmediateAction,
    aiAffectedAsset: api.aiAffectedAsset,
    // Playbook routing fields
    playbookId: api.playbookId,
    playbookName: api.playbookName,
    incidentFamily: api.incidentFamily,
    routingConfidence: api.routingConfidence,
    enrichmentCount: api.enrichmentCount,
    enrichmentLatencyMs: api.enrichmentLatencyMs,
    enrichmentFailures: api.enrichmentFailures,
    newAlertCount: api.newAlertCount ?? 0,
    newAlertsDetectedAt: api.newAlertsDetectedAt ?? null,
    reportCandidate: api.reportCandidate ?? false,
    reportCandidateBy: api.reportCandidateBy ?? null,
    reportCandidateAt: api.reportCandidateAt ?? null,
    aiEnrichment: api.summary ? {
      summary: api.summary,
      timeline: [],
      tags: [],
      riskScore: api.riskScore || 0,
      reasoning: api.reasoning || '',
      // Include additional AI fields in the enrichment too
      classification: api.aiClassification || undefined,
      confidence: api.aiConfidence || undefined,
      escalate: api.aiEscalate || undefined,
      immediateAction: api.aiImmediateAction || undefined,
      affectedAsset: api.aiAffectedAsset || undefined,
    } : undefined,
  };
}

// ============================================================================
// GET INCIDENTS
// ============================================================================

export interface IncidentPagination {
  total: number;
  limit: number;
  offset: number;
}

/**
 * `getIncidents` result: the plain array every existing caller expects, plus
 * the server's paging envelope for callers that page (the Incidents list).
 */
export type IncidentsResult = ApiResponse<EnrichedIncident[]> & {
  pagination?: IncidentPagination;
};

/**
 * Fetches incidents with optional filtering and server-side paging.
 * Omitting `limit`/`offset` keeps the server default (first 50 rows).
 */
export async function getIncidents(
  filters?: IncidentFilters
): Promise<IncidentsResult> {
  const queryParams = buildQueryString({
    tenants: filters?.tenants,
    severities: filters?.severities,
    statuses: filters?.statuses,
    dateFrom: filters?.dateFrom,
    dateTo: filters?.dateTo,
    search: filters?.search,
    assignedTo: filters?.assignedTo === null ? 'unassigned' : filters?.assignedTo,
    excludeAutoResolved: filters?.excludeAutoResolved ? 'true' : undefined,
    limit: filters?.limit,
    offset: filters?.offset,
  });

  const result = await apiFetch<PaginatedIncidentsResponse>(`/incidents${queryParams}`);
  
  if (!result.success) {
    return { data: [], success: false, error: result.error };
  }

  const incidents = result.data.incidents.map(transformApiIncident);
  return { data: incidents, success: true, pagination: result.data.pagination };
}

/**
 * Fetches a single incident by tenant and incident ID with full details
 */
export async function getIncident(
  tenantId: string,
  incidentId: string
): Promise<ApiResponse<EnrichedIncident | null>> {
  const result = await apiFetch<IncidentDetailResponse>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}`
  );
  
  if (!result.success) {
    if (result.error?.includes('404')) {
      return { data: null, success: true };
    }
    return { data: null, success: false, error: result.error };
  }

  const incident = transformApiIncident(result.data.incident);
  
  // Enrich with timeline and MITRE from detail response
  if (incident.aiEnrichment) {
    incident.aiEnrichment.timeline = result.data.timeline.map(t => ({
      title: t.title,
      timestamp: t.timestamp,
      description: t.description || '',
      confidence: t.confidence as 'high' | 'medium' | 'low' | undefined,
    }));
    incident.aiEnrichment.tags = result.data.tags;
    incident.aiEnrichment.mitre = result.data.mitre.map(m => ({
      tactic: m.tactic,
      technique: m.techniqueName || m.technique,
      techniqueId: m.technique,
    }));
  }

  return { data: incident, success: true };
}

/**
 * Gets full incident detail including all related data
 */
export async function getIncidentDetail(
  tenantId: string,
  incidentId: string
): Promise<ApiResponse<{
  incident: EnrichedIncident;
  timeline: TimelineEvent[];
  mitre: MitreMapping[];
  tags: string[];
  entities: Entity[];
  notes: AnalystNote[];
} | null>> {
  const result = await apiFetch<IncidentDetailResponse>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}`
  );
  
  if (!result.success) {
    return { data: null, success: false, error: result.error };
  }

  const incident = transformApiIncident(result.data.incident);
  
  const timeline: TimelineEvent[] = result.data.timeline.map(t => ({
    title: t.title,
    timestamp: t.timestamp,
    description: t.description || '',
    confidence: t.confidence as 'high' | 'medium' | 'low' | undefined,
  }));

  const mitre: MitreMapping[] = result.data.mitre.map(m => ({
    tactic: m.tactic,
    technique: m.techniqueName || m.technique,
    techniqueId: m.technique,
  }));

  const entities: Entity[] = result.data.entities.map(e => ({
    type: e.entityType as Entity['type'],
    name: e.displayName || e.entityValue,
    value: e.entityValue, // The raw identifier (UPN, device ID, etc.)
    details: e.additionalData as Record<string, string> | undefined,
    riskLevel: e.riskLevel,
  }));

  const notes: AnalystNote[] = result.data.notes.map(n => ({
    noteId: n.noteId,
    tenantId,
    incidentId,
    authorUserId: n.authorUserId,
    authorName: n.authorName,
    noteType: n.noteType as AnalystNote['noteType'],
    content: n.content,
    isInternal: n.isInternal,
    isPinned: n.isPinned,
    syncedToDefender: false,
    createdAt: n.createdAt,
    updatedAt: n.updatedAt,
  }));

  // Enrich incident with AI data
  if (incident.aiEnrichment) {
    incident.aiEnrichment.timeline = timeline;
    incident.aiEnrichment.tags = result.data.tags;
    incident.aiEnrichment.mitre = mitre;
  }

  return {
    data: {
      incident,
      timeline,
      mitre,
      tags: result.data.tags,
      entities,
      notes,
    },
    success: true,
  };
}

// ============================================================================
// STATUS MANAGEMENT
// ============================================================================

/**
 * Updates an incident's platform status.
 *
 * For `newStatus === 'closed'`, the analyst-api requires `classification` and
 * `determination` (added 2026-05-14 in `func-axn-analyst-api` commit 04032ba)
 * — the analyst-api uses them to resolve the Defender incident in the same
 * call. Callers MUST pass them when closing; the analyst-api returns 400
 * otherwise.
 */
export async function updateIncidentStatus(
  tenantId: string,
  incidentId: string,
  newStatus: PlatformStatus,
  reason?: string,
  classification?: DefenderClassification,
  determination?: DefenderDetermination,
): Promise<UpdateIncidentStatusResult> {
  const body: {
    status: PlatformStatus;
    reason?: string;
    classification?: DefenderClassification;
    determination?: DefenderDetermination;
  } = { status: newStatus, reason };
  if (newStatus === 'closed') {
    body.classification = classification;
    body.determination = determination;
  }

  const result = await apiPatch<UpdateStatusResponse>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/status`,
    body
  );

  if (!result.success) {
    return {
      data: null as unknown as EnrichedIncident,
      success: false,
      error: result.error,
      errorCategory: result.errorCategory,
      errorBody: result.errorBody,
    };
  }

  return {
    data: transformApiIncident(result.data.incident),
    success: true,
    defenderSync: result.data.defenderSync,
  };
}

/**
 * Gets the status history for an incident
 */
export async function getStatusHistory(
  tenantId: string,
  incidentId: string
): Promise<ApiResponse<IncidentStatusChange[]>> {
  const result = await apiFetch<StatusHistoryResponse>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/history`
  );
  
  if (!result.success) {
    return { data: [], success: false, error: result.error };
  }

  const history: IncidentStatusChange[] = result.data.history.map(h => ({
    historyId: h.historyId,
    tenantId,
    incidentId,
    previousStatus: h.previousStatus as PlatformStatus | null,
    newStatus: h.newStatus as PlatformStatus,
    changedByUserId: h.changedByUserId,
    changedByName: h.changedByName,
    actor: h.actor,
    reason: h.reason,
    changedAt: h.changedAt,
  }));

  return { data: history, success: true };
}

// ============================================================================
// AI FEEDBACK
// ============================================================================

/**
 * Submit AI accuracy feedback for an incident.
 * Used at closure (overall rating + text) and inline (thumbs up/down per AI section).
 */
export async function submitIncidentFeedback(
  tenantId: string,
  incidentId: string,
  feedback: {
    feedbackType: string;
    rating: number;
    comments?: string;
    expectedValue?: string;
    source: 'closure' | 'inline';
    idempotencyKey?: string;
  }
): Promise<ApiResponse<{ feedbackId: string }>> {
  return apiPost<{ feedbackId: string }>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/feedback`,
    feedback
  );
}

/**
 * Flag an incident as noise for this tenant.
 * Creates a pending tuning recommendation for operator review.
 */
export async function flagIncidentAsNoise(
  tenantId: string,
  incidentId: string,
  params: { classification: string; note?: string }
): Promise<ApiResponse<{ recommendationId: string }>> {
  return apiPost<{ recommendationId: string }>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/noise-flag`,
    params
  );
}

// ============================================================================
// REPORT CURATION
// ============================================================================

/**
 * Mark or unmark an incident as a candidate to feature in the customer's
 * monthly report. Nominates only — final selection + ordering + narration
 * happen on the report-build curate screen.
 */
export async function setIncidentReportCandidate(
  tenantId: string,
  incidentId: string,
  candidate: boolean,
): Promise<ApiResponse<{ reportCandidate: boolean; reportCandidateBy: string | null; reportCandidateAt: string | null }>> {
  return apiPatch<{ reportCandidate: boolean; reportCandidateBy: string | null; reportCandidateAt: string | null }>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/report-candidate`,
    { candidate },
  );
}

// ============================================================================
// ASSIGNMENT
// ============================================================================

/**
 * Assigns an incident to a user
 * Pass null to unassign
 */
export async function assignIncident(
  tenantId: string,
  incidentId: string,
  userId: string | null,
  _userName: string | null
): Promise<ApiResponse<EnrichedIncident>> {
  const result = await apiPatch<UpdateStatusResponse>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/assign`,
    { userId }
  );
  
  if (!result.success) {
    return { data: null as unknown as EnrichedIncident, success: false, error: result.error };
  }

  return { data: transformApiIncident(result.data.incident), success: true };
}

// ============================================================================
// ENTITIES / EVIDENCE
// ============================================================================

/**
 * Gets extracted entities for an incident
 */
export async function getIncidentEntities(
  tenantId: string,
  incidentId: string
): Promise<ApiResponse<Entity[]>> {
  const result = await apiFetch<EntitiesResponse>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/entities`
  );
  
  if (!result.success) {
    return { data: [], success: false, error: result.error };
  }

  const entities: Entity[] = result.data.entities.map(e => ({
    type: e.entityType as Entity['type'],
    name: e.displayName || e.entityValue,
    value: e.entityValue, // The raw identifier (UPN, device ID, etc.)
    details: e.additionalData as Record<string, string> | undefined,
    riskLevel: e.riskLevel,
  }));

  return { data: entities, success: true };
}

// ============================================================================
// RAW DATA
// ============================================================================

/**
 * Gets the raw Defender incident data
 * Note: This would typically fetch from blob storage via a signed URL
 */
export async function getRawIncident(
  _tenantId: string,
  _incidentId: string
): Promise<ApiResponse<unknown | null>> {
  // TODO: Implement raw blob fetching when needed
  // For now, return null as this requires blob storage access
  return { data: null, success: true };
}

// ============================================================================
// BULK OPERATIONS
// ============================================================================

export interface BulkCloseRequest {
  incidents: Array<{ tenantId: string; incidentId: string }>;
  classification: string;
  determination: string;
  closingNote?: string;
  linkGroupName?: string;
}

export interface BulkCloseResult {
  results: Array<{
    tenantId: string;
    incidentId: string;
    success: boolean;
    error?: string;
  }>;
  successCount: number;
  failureCount: number;
  linkGroupId?: string;
}

/**
 * Bulk close multiple incidents with the same classification and determination.
 * Will also sync to Defender for each customer tenant.
 */
export async function bulkCloseIncidents(
  request: BulkCloseRequest
): Promise<ApiResponse<BulkCloseResult>> {
  const result = await apiPost<BulkCloseResult>('/incidents/bulk/close', request);
  return result;
}

export interface BulkAssignRequest {
  incidents: Array<{ tenantId: string; incidentId: string }>;
  userId: string | null;
}

export interface BulkAssignResult {
  results: Array<{
    tenantId: string;
    incidentId: string;
    success: boolean;
    error?: string;
  }>;
  successCount: number;
  failureCount: number;
}

/**
 * Bulk assign multiple incidents to a user (or unassign if userId is null)
 */
export async function bulkAssignIncidents(
  request: BulkAssignRequest
): Promise<ApiResponse<BulkAssignResult>> {
  const result = await apiPost<BulkAssignResult>('/incidents/bulk/assign', request);
  return result;
}

export interface BulkLinkRequest {
  incidents: Array<{ tenantId: string; incidentId: string }>;
  linkGroupName: string;
}

export interface BulkLinkResult {
  linkGroupId: string;
  incidentCount: number;
}

/**
 * Link multiple incidents together under a common group
 */
export async function bulkLinkIncidents(
  request: BulkLinkRequest
): Promise<ApiResponse<BulkLinkResult>> {
  const result = await apiPost<BulkLinkResult>('/incidents/bulk/link', request);
  return result;
}

// ============================================================================
// INCIDENT COUNTS
// ============================================================================

export interface IncidentCounts {
  allOpen: number;
  myTickets: number;
  unassigned: number;
  closed: number;
}

/**
 * Get incident counts for the sidebar quick filters
 */
export async function getIncidentCounts(
  currentUserId?: string
): Promise<ApiResponse<IncidentCounts>> {
  const params = currentUserId ? `?userId=${encodeURIComponent(currentUserId)}` : '';
  const result = await apiFetch<IncidentCounts>(`/incidents/counts${params}`);
  return result;
}

export async function clearIncidentNewAlerts(
  tenantId: string,
  incidentId: string
): Promise<ApiResponse<void>> {
  return apiFetch<void>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/new-alerts/clear`,
    { method: 'POST' }
  );
}
